import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useStudentsQuery } from "@/hooks/useStudents";
import { studentWorkspacePaths } from "@/utils/student-workspace";

const WORKSPACE_TABS = [
  { key: "overview", label: "概览" },
  { key: "assessments", label: "评估" },
  { key: "reports", label: "报告" },
  { key: "parent", label: "家长报告" },
  { key: "iep", label: "IEP 计划" },
] as const;

type WorkspaceTab = (typeof WORKSPACE_TABS)[number]["key"];

export function StudentWorkspaceLayout(): JSX.Element {
  const { studentId = "" } = useParams<{ studentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const studentsQuery = useStudentsQuery();
  const student = studentsQuery.data?.find((item) => item.id === studentId);
  const currentTab = resolveCurrentTab(location.pathname);

  if (studentsQuery.isLoading) {
    return <WorkspaceMessage title="正在加载儿童信息…" />;
  }

  if (studentsQuery.isError) {
    return (
      <WorkspaceMessage
        title="儿童信息加载失败"
        description="请检查网络后重试。"
        actionLabel="重新加载"
        onAction={() => void studentsQuery.refetch()}
      />
    );
  }

  if (!student) {
    return (
      <WorkspaceMessage
        title="未找到该儿童"
        description="这条链接中的儿童不存在，或你没有查看权限。"
        actionLabel="返回全部儿童"
        actionTo={studentWorkspacePaths.children}
      />
    );
  }

  const ageLabel = student.profile.age == null ? "年龄未填写" : `${student.profile.age} 岁`;
  const classroom = student.enrollment.classroom || "班级未填写";

  return (
    <div data-student-workspace className="min-h-full min-w-0">
      <section
        data-app-chrome
        aria-label="当前儿童工作空间"
        className="border-b border-slate-200/80 bg-white px-4 pt-4 shadow-[0_8px_24px_-22px_rgba(51,65,85,0.55)] md:px-6 md:pt-5"
      >
        <div className="mx-auto max-w-[1560px]">
          <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-accent-fresh text-lg font-semibold text-white shadow-sm"
                aria-hidden="true"
              >
                {student.profile.name.slice(0, 1)}
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-xl font-semibold text-slate-800">
                  {student.profile.name}
                </h1>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  <Link to="/children" className="font-medium text-brand hover:underline">
                    全部儿童
                  </Link>
                  <span aria-hidden="true"> / </span>
                  {ageLabel} · {classroom}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label
                htmlFor="workspace-student-switcher"
                className="shrink-0 text-sm font-medium text-slate-600"
              >
                切换儿童
              </label>
              <select
                id="workspace-student-switcher"
                value={student.id}
                onChange={(event) => {
                  const activeTab = resolveCurrentTab(window.location.pathname);
                  navigate(pathForTab(event.target.value, activeTab), { replace: false });
                }}
                className="min-h-11 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-56 sm:flex-none"
              >
                {(studentsQuery.data ?? []).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.profile.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <nav aria-label="儿童工作区导航" className="-mb-px flex min-w-0 gap-1 overflow-x-auto">
            {WORKSPACE_TABS.map((tab) => (
              <NavLink
                key={tab.key}
                to={pathForTab(student.id, tab.key)}
                aria-current={currentTab === tab.key ? "page" : undefined}
                className={({ isActive }) =>
                  `inline-flex min-h-11 shrink-0 items-center border-b-2 px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                    isActive
                      ? "border-brand text-brand-dark"
                      : "border-transparent text-slate-500 hover:border-brand/30 hover:text-brand-dark"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </section>
      <div data-student-outlet className="mx-auto max-w-[1560px] p-4 md:p-6">
        <Outlet />
      </div>
    </div>
  );
}

function resolveCurrentTab(pathname: string): WorkspaceTab {
  const candidate = pathname.split("/").filter(Boolean)[2];
  return WORKSPACE_TABS.some((tab) => tab.key === candidate)
    ? (candidate as WorkspaceTab)
    : "overview";
}

function pathForTab(studentId: string, tab: WorkspaceTab): string {
  switch (tab) {
    case "assessments":
      return studentWorkspacePaths.assessments(studentId);
    case "reports":
      return studentWorkspacePaths.reports(studentId);
    case "parent":
      return studentWorkspacePaths.parentReport(studentId);
    case "iep":
      return studentWorkspacePaths.iep(studentId);
    default:
      return studentWorkspacePaths.overview(studentId);
  }
}

function WorkspaceMessage({
  title,
  description,
  actionLabel,
  actionTo,
  onAction,
}: {
  title: string;
  description?: string;
  actionLabel?: string;
  actionTo?: string;
  onAction?: () => void;
}): JSX.Element {
  const buttonClass =
    "mt-5 inline-flex min-h-11 items-center justify-center rounded-lg bg-brand px-5 text-sm font-semibold text-white hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2";
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <section className="w-full max-w-xl rounded-2xl border border-brand/20 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-800">{title}</h1>
        {description ? <p className="mt-2 text-sm text-slate-600">{description}</p> : null}
        {actionTo && actionLabel ? (
          <Link to={actionTo} className={buttonClass}>
            {actionLabel}
          </Link>
        ) : null}
        {onAction && actionLabel ? (
          <button type="button" onClick={onAction} className={buttonClass}>
            {actionLabel}
          </button>
        ) : null}
      </section>
    </div>
  );
}
